import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, ModalWrapper, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import type { ContactBankAccountListItem } from "@msuite/utils/types";
import React from "react";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { EditContactBankAccountModal } from "@/modals/contacts/edit-contact-bank-account-modal";

/** Editing and removing an account; both change the contact, so both need `update Contact`. */
export function RowActions({ cell }: CellContext<ContactBankAccountListItem, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const { citadel } = useCitadel();
	const editModal = useDisclosure();
	const confirmModal = useAsyncModal();

	/** Functions */
	async function handleOnDelete() {
		if (!(await confirmModal.confirm())) return;
		const response = await client.contacts[":contactId"]["bank-accounts"][":bankAccountId"].$delete(
			{ param: { contactId: account.contactId, bankAccountId: account.id } },
		);
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["contact", "incoming-invoices"]) });
	}

	/** Computed */
	const account = cell.row.original;
	const canUpdate = citadel?.can("update", "Contact") === true;

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper>
				{canUpdate && (
					<Menu
						items={createStandardMenuItems({
							onEdit: editModal.onOpen,
							onDelete: handleOnDelete,
						})}
					>
						<Button
							size="xxs"
							icon={<Icon name="EllipsisVertical" />}
							variant="ghost"
						/>
					</Menu>
				)}
			</RowActionsWrapper>
			<ModalWrapper>
				{editModal.isOpen && (
					<EditContactBankAccountModal
						isOpen={editModal.isOpen}
						onClose={editModal.onClose}
						contactId={account.contactId}
						bankAccountId={account.id}
					/>
				)}
				{confirmModal.isOpen && (
					<ConfirmModal
						controller={confirmModal}
						message={t("contacts.bank_accounts.delete_confirm_message")}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
